/**
 * Offline Cache Service (CORA-164)
 *
 * Persists a per-patient, read-only snapshot of the patient portal's
 * list/summary views to IndexedDB so an installed patient can still view their
 * recent records with no connection. On each SUCCESSFUL online load a page
 * writes its section into the snapshot; when a network request FAILS (offline)
 * the page falls back to the cached section and renders it read-only behind an
 * "offline / last synced" banner.
 *
 * DATA-PROTECTION BOUND (hard requirement): the snapshot stores ONLY the same
 * list/summary fields the LIST views render — last 6 months of appointments,
 * the medical-record timeline SUMMARY (encounters / lab-result summaries /
 * referrals / prescription summaries), and the unread message count. It must
 * NEVER cache full document or attachment bodies, clinical note bodies, lab
 * result payloads, or any presigned file URL. Over-caching PHI offline is the
 * central risk of this story, so the write helpers deliberately pick a fixed
 * field subset rather than persisting whole API DTOs verbatim.
 *
 * Storage is the native IndexedDB API (no runtime dependency). All access goes
 * through a single object store keyed by the signed-in account's id (the key from
 * `useOfflineSnapshotKey`); ids are only ever used as a storage key, never
 * displayed (the cached views render `patientCode`). The whole store is dropped
 * on logout so a shared device does not leak the previous patient's snapshot.
 */

import type { Appointment } from './types';
import type { PortalEncounterItem, PortalLabResultItem, PortalReferralItem, PortalPrescriptionItem } from './api/patientPortalApi';
import type { SpecialtyNameTranslations } from './api/specialtiesApi';

const DB_NAME = 'coraflow-offline';
const DB_VERSION = 1;
const STORE_NAME = 'patient-snapshots';

/** Appointments older than this are dropped before caching (Andre's scope). */
const APPOINTMENT_RETENTION_MONTHS = 6;

/**
 * The appointment fields the portal LIST views render. A deliberate subset of
 * the full `Appointment` DTO — no clinical bodies, attachments, or URLs.
 */
export interface CachedAppointment {
  id: string;
  patientCode: string | null;
  doctorName: string;
  specialty: string;
  date: string;
  time: string;
  type: string;
  status: Appointment['status'];
  room: string | null;
  notes: string | null;
  /** Gates the read-only/contact-desk hint on the cached card. */
  hasActiveEncounter: boolean;
}

/** Timeline encounter summary (state + dates only — no note bodies). */
export interface CachedEncounter {
  id: string;
  currentState: string;
  createdAt: string;
}

/** Lab/exam result SUMMARY only — the short summary text, never the payload. */
export interface CachedLabResult {
  id: string;
  examType: string;
  resultDate: string;
  summaryText: string | null;
}

/** Referral summary fields the timeline renders. */
export interface CachedReferral {
  id: string;
  status: string;
  referralDate: string;
  referredToSpecialty: string;
  reasonForReferral: string;
  referringDoctorName: string | null;
}

/** A single medication line as shown in the medications list. */
export interface CachedPrescriptionMedication {
  id: string;
  medicationName: string;
  dosage: string;
  frequency: string;
}

/** Prescription SUMMARY (counters + medication lines the list renders). */
export interface CachedPrescription {
  id: string;
  status: string;
  prescribedDate: string;
  doctorName: string | null;
  refillsRemaining: number;
  medications: CachedPrescriptionMedication[];
}

/** Catalog names of the specialties a section lists, by slug: after a reload with no connection the catalog cannot be read. */
export type CachedSpecialtyNames = Record<string, SpecialtyNameTranslations>;

/** The medical-history timeline + medications snapshot section. */
export interface CachedMedicalHistory {
  patientCode: string | null;
  encounters: CachedEncounter[];
  labResults: CachedLabResult[];
  referrals: CachedReferral[];
  prescriptions: CachedPrescription[];
  specialtyNames?: CachedSpecialtyNames;
}

/**
 * One per-patient record. Sections are composed across page loads, so each
 * carries its own `syncedAt` ISO timestamp for the per-view "last synced"
 * banner; a section is absent until that page has loaded online at least once.
 */
export interface PatientSnapshot {
  /** The snapshot key (the account id; the field keeps its first name, the store's keyPath) — never displayed. */
  patientId: string;
  appointments?: {
    items: CachedAppointment[];
    syncedAt: string;
    specialtyNames?: CachedSpecialtyNames;
  };
  medicalHistory?: {
    data: CachedMedicalHistory;
    syncedAt: string;
  };
  unreadMessages?: {
    count: number;
    syncedAt: string;
  };
}

/** True when IndexedDB is unavailable (SSR / private-mode / old browsers). */
function indexedDbUnavailable(): boolean {
  return typeof indexedDB === 'undefined';
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'patientId' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function runTransaction<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, mode);
        const request = work(tx.objectStore(STORE_NAME));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        tx.oncomplete = () => db.close();
        tx.onabort = () => {
          db.close();
          reject(tx.error);
        };
      }),
  );
}

function readSnapshot(snapshotKey: string): Promise<PatientSnapshot | null> {
  return runTransaction<PatientSnapshot | undefined>('readonly', (store) => store.get(snapshotKey)).then((row) => row ?? null);
}

/**
 * Merge a partial section update into the patient's record so the three pages
 * can each write their own slice without clobbering the others. Read-modify-
 * write inside one logical operation; the per-patient row is the unit.
 */
async function mergeSnapshot(snapshotKey: string, patch: Partial<Omit<PatientSnapshot, 'patientId'>>): Promise<void> {
  if (indexedDbUnavailable() || !snapshotKey) return;
  const existing = (await readSnapshot(snapshotKey)) ?? { patientId: snapshotKey };
  const next: PatientSnapshot = { ...existing, ...patch, patientId: snapshotKey };
  await runTransaction('readwrite', (store) => store.put(next));
}

/**
 * Keep only appointments whose calendar day is within the retention window.
 * Compared as 'YYYY-MM-DD' strings (apt.date is a civil date) to avoid the
 * UTC-vs-local skew the page documents.
 */
function withinRetention(appointments: Appointment[]): Appointment[] {
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - APPOINTMENT_RETENTION_MONTHS);
  const cutoffStr = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}-${String(cutoff.getDate()).padStart(2, '0')}`;
  return appointments.filter((apt) => apt.date >= cutoffStr);
}

/** Plain copies of the catalog names: the catalog hands over reactive proxies, which IndexedDB cannot clone. */
function toCachedSpecialtyNames(names: CachedSpecialtyNames | undefined): CachedSpecialtyNames {
  return Object.fromEntries(Object.entries(names ?? {}).map(([slug, translations]) => [slug, { ...translations }]));
}

function toCachedAppointment(apt: Appointment): CachedAppointment {
  return {
    id: apt.id,
    patientCode: apt.patientCode ?? null,
    doctorName: apt.doctorName,
    specialty: apt.specialty,
    date: apt.date,
    time: apt.time,
    type: apt.type,
    status: apt.status,
    room: apt.room ?? null,
    notes: apt.notes ?? null,
    hasActiveEncounter: apt.hasActiveEncounter ?? false,
  };
}

export const offlineCacheService = {
  /**
   * Persist the appointments section. Filters to the last 6 months and stores
   * only the list fields — never the full DTO — plus the catalog names of their
   * specialties. Failures are swallowed: caching is best-effort and must never
   * break the online render.
   */
  async saveAppointments(snapshotKey: string, appointments: Appointment[], specialtyNames: CachedSpecialtyNames = {}): Promise<void> {
    try {
      const items = withinRetention(appointments).map(toCachedAppointment);
      await mergeSnapshot(snapshotKey, {
        appointments: { items, syncedAt: new Date().toISOString(), specialtyNames: toCachedSpecialtyNames(specialtyNames) },
      });
    } catch {
      // best-effort cache; ignore write failures
    }
  },

  /**
   * Persist the medical-history timeline + medications SUMMARY. Picks a fixed
   * subset of each section so no clinical note body, lab payload, attachment, or
   * presigned URL is ever written (data-protection bound).
   */
  async saveMedicalHistory(
    snapshotKey: string,
    input: {
      patientCode: string | null;
      encounters: PortalEncounterItem[];
      labResults: PortalLabResultItem[];
      referrals: PortalReferralItem[];
      prescriptions: PortalPrescriptionItem[];
      specialtyNames?: CachedSpecialtyNames;
    },
  ): Promise<void> {
    try {
      const data: CachedMedicalHistory = {
        patientCode: input.patientCode,
        specialtyNames: toCachedSpecialtyNames(input.specialtyNames),
        encounters: input.encounters.map((e) => ({ id: e.id, currentState: e.currentState, createdAt: e.createdAt })),
        labResults: input.labResults.map((r) => ({
          id: r.id,
          examType: r.examType,
          resultDate: r.resultDate,
          summaryText: r.summaryText,
        })),
        referrals: input.referrals.map((r) => ({
          id: r.id,
          status: r.status,
          referralDate: r.referralDate,
          referredToSpecialty: r.referredToSpecialty,
          reasonForReferral: r.reasonForReferral,
          referringDoctorName: r.referringDoctorName || null,
        })),
        prescriptions: input.prescriptions.map((p) => ({
          id: p.id,
          status: p.status,
          prescribedDate: p.prescribedDate,
          doctorName: p.doctorName || null,
          refillsRemaining: p.refillsRemaining,
          medications: p.medications.map((m) => ({
            id: m.id,
            medicationName: m.medicationName,
            dosage: m.dosage,
            frequency: m.frequency,
          })),
        })),
      };
      await mergeSnapshot(snapshotKey, { medicalHistory: { data, syncedAt: new Date().toISOString() } });
    } catch {
      // best-effort cache; ignore write failures
    }
  },

  /** Persist the unread message count summary (a single integer). */
  async saveUnreadMessageCount(snapshotKey: string, count: number): Promise<void> {
    try {
      await mergeSnapshot(snapshotKey, { unreadMessages: { count, syncedAt: new Date().toISOString() } });
    } catch {
      // best-effort cache; ignore write failures
    }
  },

  /** Read the full per-patient snapshot, or null when none/unavailable. */
  async getSnapshot(snapshotKey: string): Promise<PatientSnapshot | null> {
    if (indexedDbUnavailable() || !snapshotKey) return null;
    try {
      return await readSnapshot(snapshotKey);
    } catch {
      return null;
    }
  },

  /**
   * Drop every cached snapshot. Called from the auth store on logout so a
   * shared device never leaks the previous patient's records. Best-effort.
   */
  async clear(): Promise<void> {
    if (indexedDbUnavailable()) return;
    try {
      await runTransaction('readwrite', (store) => store.clear());
    } catch {
      // ignore — nothing actionable on a failed cache clear
    }
  },
};

export default offlineCacheService;
